import React, { useState } from "react";

/**
 * MediaEmbed — renders a video, PDF embed, or image with an optional
 * English context card above it (for French presentations).
 *
 * Props:
 *   src         — public path to the file (e.g. "/projects/demo.mp4")
 *   type        — "video" | "pdf" | "image"
 *   label       — optional title shown above the embed
 *   contextNote — optional English description shown in a context card above
 *   lang        — optional "fr" flag to show "Slides in French" badge
 */
const MediaEmbed = ({ src, type, label, contextNote, lang }) => {
  const [expanded, setExpanded] = useState(false);

  if (!src) return null;

  return (
    <div style={{ marginTop: 14, marginBottom: 4 }}>
      {/* Context card for French presentations */}
      {contextNote && (
        <div style={{
          background: "rgba(199,125,255,0.07)",
          border: "1px solid rgba(199,125,255,0.18)",
          borderRadius: 10,
          padding: "10px 14px",
          marginBottom: 10,
          fontSize: "0.78rem",
          color: "rgba(255,235,250,0.72)",
          lineHeight: 1.55,
        }}>
          {lang === "fr" && (
            <span style={{
              display: "inline-block",
              marginBottom: 5,
              fontSize: "0.68rem",
              letterSpacing: "0.08em",
              color: "rgba(255,179,217,0.65)",
              fontFamily: "'Cinzel', serif",
            }}>
              📋 PRESENTATION IN FRENCH — CONTEXT BELOW
            </span>
          )}
          {lang === "fr" && <br />}
          {contextNote}
        </div>
      )}

      {/* Toggle button */}
      <button
        onClick={() => setExpanded(v => !v)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          background: expanded ? "rgba(255,179,217,0.10)" : "rgba(255,179,217,0.06)",
          border: "1px solid rgba(255,179,217,0.20)",
          borderRadius: 10,
          color: "rgba(255,235,250,0.78)",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: "0.78rem",
          letterSpacing: "0.04em",
          padding: "8px 14px",
          cursor: "pointer",
          width: "100%",
          transition: "all 0.2s",
          marginBottom: expanded ? 8 : 0,
        }}
      >
        <span>{type === "video" ? "▶" : type === "pdf" ? "📄" : "🖼"}</span>
        <span>{label || (type === "video" ? "Watch video" : type === "pdf" ? "View presentation" : "View image")}</span>
        <span style={{ marginLeft: "auto", opacity: 0.55 }}>{expanded ? "▲" : "▼"}</span>
      </button>

      {/* Embed */}
      {expanded && (
        <div style={{
          borderRadius: 10,
          overflow: "hidden",
          border: "1px solid rgba(255,179,217,0.14)",
          background: "rgba(0,0,0,0.3)",
        }}>
          {type === "video" && (
            <video
              controls
              style={{ width: "100%", display: "block", maxHeight: 340 }}
              preload="metadata"
            >
              <source src={src} type="video/mp4" />
              Your browser does not support video.
            </video>
          )}

          {type === "pdf" && (
            <embed
              src={src}
              type="application/pdf"
              width="100%"
              height="420"
              style={{ display: "block" }}
            />
          )}

          {type === "image" && (
            <img
              src={src}
              alt={label || ""}
              style={{ width: "100%", display: "block", maxHeight: 340, objectFit: "contain" }}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          )}
        </div>
      )}
    </div>
  );
};

export default MediaEmbed;
